import {
  addDefaultProps,
  addDisplayName,
  addEffect,
  addHandlers,
  addProps,
  addState,
  addStateHandlers,
  flowMax,
} from 'ad-hok'
import { FC } from 'react'
import { addApi, addUser } from 'shared/src/api'
import { createUseStyles } from 'react-jss'
import { addDataLoadingFromPromise } from 'shared/src/utils/dataLoading'
import { Trans } from 'react-i18next'
import { typedAs } from 'shared/src/utils/typedAs'
import { addNavigate } from 'shared/src/utils/addNavigate'
import { UserInput } from 'shared/src/api/types'
import { addDebouncedCopy, addPropTrackingRef } from 'ad-hok-utils'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { addCmsLoadingAndErrorIndicators } from '../../utils/addCmsLoadingAndErrorIndicators'
import { Page } from '../Page'
import { TextAreaNew, TextFieldNew } from '../TextField'
import {
  BODY_MEDIUM_STYLE,
  MAIN_TITLE_STYLE,
  NOTES_MEDIUM_STYLE,
  SANS_SERIF_FONT_FAMILY,
  SECONDARY_TITLE_STYLE,
} from '../styles'
import { UrlField } from './UrlField'
import { IconName } from '../Icon'
import { addToastContext } from '../toastContext'
import { NavButton } from '../NavButton'
import { userValidator } from './validator'
import { hasErrors, validate } from '../../utils/validators'
import phoneNumberSwooshBackground from '../../assets/phone-number-swoosh-background.svg'
import { LAYOUT_PADDING_VERTICAL } from '../Layout'
import { ImagePicker } from '../ContentPicker'
import { CustomLinks } from './CustomLinks'
import { SIDE_PADDING } from '../PageConstants'
import { makeShopperProfileUrl } from 'utils/routes'

export const MAX_BIO_LENGTH = 175

const SOCIAL_URL_KEYS = [
  'instagramUrl',
  'tiktokUrl',
  'amazonUrl',
  'spotifyUrl',
  'pinterestUrl',
  'snapchatUrl',
  'goodreadsUrl',
  'etsyUrl',
  'youtubeUrl',
  'twitterUrl',
  'facebookUrl',
  'applePodcastsUrl',
] as const

const INSTAGRAM_URL_KEY = 'instagramUrl'

type SocialUrlKey = (typeof SOCIAL_URL_KEYS)[number]

const SOCIAL_URL_ICONS: Record<SocialUrlKey, IconName> = {
  instagramUrl: 'instagram',
  tiktokUrl: 'tikTok',
  amazonUrl: 'amazon',
  spotifyUrl: 'spotify',
  pinterestUrl: 'pinterest',
  snapchatUrl: 'snapchat',
  goodreadsUrl: 'goodreads',
  etsyUrl: 'etsy',
  youtubeUrl: 'youtube',
  twitterUrl: 'twitter',
  facebookUrl: 'facebook',
  applePodcastsUrl: 'applePodcasts',
}

const useStyles = createUseStyles({
  pageContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
  },
  pageButtonsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  viewProfileText: {
    fontFamily: SANS_SERIF_FONT_FAMILY,
    fontWeight: 700,
    color: '#90734F',
  },
  title: MAIN_TITLE_STYLE,
  sectionTitle: {
    ...SECONDARY_TITLE_STYLE,
    marginBottom: 11,
    marginTop: 20,
  },
  userCustomLinksTitle: {
    fontFamily: SANS_SERIF_FONT_FAMILY,
    fontWeight: 700,
    fontSize: 16,
    lineHeight: '140%',
    letterSpacing: '0.02em',
    color: 'rgba(54, 52, 50, 0.96)',
    marginBottom: 6,
    marginTop: 15,
  },
  userCustomLinksDescription: {
    ...NOTES_MEDIUM_STYLE,
    color: '#90734F',
    marginBottom: 24,
  },
  pins: {
    marginBottom: 56,
  },
  sectionDescription: {
    ...BODY_MEDIUM_STYLE,
    color: '#90734F',
    opacity: '56%',
    maxWidth: 300,
    marginBottom: 15,
  },
  socialsMax: {
    fontWeight: 700,
  },
  websiteField: {
    marginBottom: 20,
  },
  phoneNumberOuterContainer: {
    marginTop: 40,
    marginBottom: -LAYOUT_PADDING_VERTICAL,
    width: `calc(100% + ${2 * SIDE_PADDING}px)`,
    alignSelf: 'center',
  },
  phoneNumberSwoosh: {
    backgroundImage: `url(${phoneNumberSwooshBackground})`,
    backgroundSize: 'cover',
    height: 40,
  },
  phoneNumberInnerContainer: {
    backgroundColor: 'rgba(235, 202, 180, 0.2)',
    paddingLeft: SIDE_PADDING,
    paddingRight: SIDE_PADDING,
    paddingTop: 15,
    paddingBottom: 10,
  },
  phoneNumberTitle: {
    ...SECONDARY_TITLE_STYLE,
    color: '#363432',
    marginBottom: 11,
  },
  phoneNumberDescription: {
    ...NOTES_MEDIUM_STYLE,
    color: '#90734F',
    marginBottom: 16,
    maxWidth: 317,
  },
  phoneNumberDescriptionBold: {
    fontWeight: 700,
  },
  imagePicker: {
    backgroundColor: 'transparent',
  },
  imageDescription: {
    fontSize: 14,
    color: '#90734F',
    marginBottom: 10,
  },
  pinPreviewImage: {
    marginBottom: 10,
  },
})

const NORMALIZED_PHONE_NUMBER_IGNORED_CHARACTERS_REGEX = /[\D]/g

const urlWithScheme = (url: string): string =>
  url.length < 5 || url.indexOf('http') === 0 ? url : `https://${url}`

type Step = 'all' | 'user' | 'personal' | 'socials' | 'pins' | 'instagram'
const ONBOARDING_STEPS: Step[] = ['user', 'personal', 'socials', 'pins']

interface Props {
  firstStep?: Step
  onOnboardingFinished?: () => void
}

export const Account: FC<Props> = flowMax(
  addDisplayName('Account'),
  addApi,
  addUser,
  addProps(({ user }) => ({
    shopperProfileUrl: user.username
      ? makeShopperProfileUrl(user.username)
      : undefined,
  })),
  addDataLoadingFromPromise(
    'cmsUser',
    ({ api, user: { id } }) => api.users.get(id),
    [],
    ({ user: { id } }) => `account.user-${id}`
  ),
  addDataLoadingFromPromise(
    'customLinks',
    ({ user, api }) => api.customLinks.getForUserId(user.id),
    []
  ),
  addCmsLoadingAndErrorIndicators('cmsUser'),
  addCmsLoadingAndErrorIndicators('customLinks'),
  addState('step', 'setStep', ({ firstStep }) => firstStep ?? 'all'),
  addDefaultProps({
    firstStep: 'all',
  }),
  addStateHandlers(
    ({ cmsUser: { id, createdAt, updatedAt, ...user } }) => {
      const phoneNumber = user.phoneNumber?.replace('+1', '') ?? null

      return {
        input: { ...typedAs<UserInput>(user), phoneNumber },
        hasUsernameUniquenessError: false,
        hasTakenAction: false,
      }
    },
    {
      setUsernameUniquenessError: () => () => ({
        hasUsernameUniquenessError: true,
      }),
      setHasTakenAction: () => () => ({
        hasTakenAction: true,
      }),
      setUsername:
        ({ input }) =>
        (username: string) => ({
          input: { ...input, username },
          username,
          hasUsernameUniquenessError: false,
        }),
      setPhoneNumber:
        ({ input }) =>
        (phoneNumber: string) => {
          const normalizedPhoneNumber = phoneNumber.replace(
            NORMALIZED_PHONE_NUMBER_IGNORED_CHARACTERS_REGEX,
            ''
          )
          return {
            input: { ...input, phoneNumber: normalizedPhoneNumber },
          }
        },
      setBio:
        ({ input }) =>
        (bio: string) => ({
          input: { ...input, bio: bio.substring(0, MAX_BIO_LENGTH) },
        }),
      toggleCategoryId:
        ({ input }) =>
        (id: number) => {
          const ids = new Set(input.categoryIds)

          if (ids.has(id)) {
            ids.delete(id)
          } else {
            ids.add(id)
          }

          return {
            input: { ...input, categoryIds: Array.from(ids) },
          }
        },
      setHomepage:
        ({ input }) =>
        (url: string) => ({
          input: { ...input, homepage: urlWithScheme(url) },
        }),
      setSocialUrl:
        ({ input }) =>
        (key: SocialUrlKey, url: string) => {
          const completeUrl = urlWithScheme(url)
          return {
            input: { ...input, [key]: completeUrl },
          }
        },
      setImageId:
        ({ input }) =>
        (imageId: number | null) => ({
          input: { ...input, imageId },
        }),
      setPublicEmail:
        ({ input }) =>
        (publicEmail: string) => ({
          input: { ...input, publicEmail },
        }),
    }
  ),
  addProps(({ input }) => ({
    usernameForDebouncing: input.username ?? '',
  })),
  addDebouncedCopy(250, 'usernameForDebouncing', 'debouncedUsername'),
  addPropTrackingRef('usernameForDebouncing', 'usernameRef'),
  addEffect(
    ({ debouncedUsername, usernameRef, api, setUsernameUniquenessError }) =>
      () => {
        if (debouncedUsername.length === 0) {
          return
        }

        api.users
          .validateUsername(debouncedUsername)
          .then((response) => {
            if (
              debouncedUsername !== usernameRef.current ||
              (response.unique === true &&
                response.valid === true &&
                response.reserved === false)
            ) {
              return
            }

            setUsernameUniquenessError()
          })
          .catch(() => {})
      },
    ['debouncedUsername']
  ),
  addTranslationHelpers,
  addProps(
    ({ t }) => ({
      validator: userValidator(t),
    }),
    ['t']
  ),
  addProps(({ validator, input }) => ({
    errors: validate(validator, input),
  })),
  addToastContext,
  addNavigate,
  addHandlers({
    onSave:
      ({
        cmsUser,
        api,
        input,
        onOnboardingFinished,
        step,
        setHasTakenAction,
        refetchUser,
        errors,
        hasUsernameUniquenessError,
        navigate,
        showSuccessToast,
        showErrorToast,
        t,
      }) =>
      () => {
        setHasTakenAction()
        if (hasErrors(errors) || hasUsernameUniquenessError) {
          showErrorToast({
            title: t('admin.account.saveError'),
            details: t('admin.account.validationError'),
          })

          return
        }

        const normalizedPhoneNumber = input.phoneNumber
          ? `+1${input.phoneNumber}`
          : null

        api.users
          .update(cmsUser.id, {
            ...input,
            phoneNumber: normalizedPhoneNumber,
          })
          .then(() => {
            if (step === 'pins' && onOnboardingFinished) {
              onOnboardingFinished()
            } else if (step === 'instagram') {
              navigate('/posts')
            } else {
              showSuccessToast({
                title: t('admin.account.saveSuccessTitle'),
                details: t('admin.account.saveSuccessDetails'),
              })
            }
            refetchUser()
          })
          .catch(() => {
            showErrorToast({
              title: t('admin.account.saveError'),
              details: t('admin.general.genericError'),
            })
          })
      },
    onNext:
      ({
        step,
        setStep,
        setHasTakenAction,
        errors,
        showErrorToast,
        hasUsernameUniquenessError,
        t,
      }) =>
      () => {
        setHasTakenAction()
        if (hasErrors(errors) || hasUsernameUniquenessError) {
          showErrorToast({
            title: t('admin.account.saveError'),
            details: t('admin.account.validationError'),
          })

          return
        }
        setStep(
          ONBOARDING_STEPS[ONBOARDING_STEPS.findIndex((s) => s === step) + 1]
        )
      },
    onBack:
      ({
        step,
        setStep,
        setHasTakenAction,
        errors,
        showErrorToast,
        hasUsernameUniquenessError,
        t,
      }) =>
      () => {
        setHasTakenAction()
        if (hasErrors(errors) || hasUsernameUniquenessError) {
          showErrorToast({
            title: t('admin.account.saveError'),
            details: t('admin.account.validationError'),
          })

          return
        }
        setStep(
          ONBOARDING_STEPS[ONBOARDING_STEPS.findIndex((s) => s === step) - 1]
        )
      },
  }),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({
    user,
    step,
    shopperProfileUrl,
    hasTakenAction,
    hasUsernameUniquenessError,
    input,
    input: { username, bio, phoneNumber, homepage, imageId, publicEmail },
    setUsername,
    setPhoneNumber,
    setBio,
    customLinks,
    setHomepage,
    setSocialUrl,
    setPublicEmail,
    onSave,
    onNext,
    onBack,
    setImageId,
    errors,
    classes,
    t,
  }) => (
    <Page
      leftButton={step === 'all' || step === 'instagram'}
      renderExtraBarContent={() => (
        <div className={classes.pageContainer}>
          {step === 'all' && (
            <NavButton title={t('admin.account.save')} onClick={onSave} />
          )}
          {step === 'user' && (
            <NavButton title={t('admin.account.next')} onClick={onNext} />
          )}
          {(step === 'personal' || step === 'socials') && (
            <div className={classes.pageButtonsContainer}>
              <NavButton title={t('admin.account.back')} onClick={onBack} />
              <NavButton title={t('admin.account.next')} onClick={onNext} />
            </div>
          )}
          {step === 'instagram' && (
            <NavButton title={t('admin.account.save')} onClick={onSave} />
          )}
          {step === 'pins' && (
            <div className={classes.pageButtonsContainer}>
              <NavButton title={t('admin.account.back')} onClick={onBack} />
              <NavButton title={t('admin.account.save')} onClick={onSave} />
            </div>
          )}
        </div>
      )}
    >
      {(step === 'all' || step === 'user') && (
        <div>
          <div className={classes.header}>
            <div>
              <h1 className={classes.title}>{t('admin.account.title')}</h1>
              {shopperProfileUrl && step === 'all' && (
                <a
                  href={shopperProfileUrl}
                  target={'_blank'}
                  rel="noreferrer"
                  className={classes.viewProfileText}
                >
                  {t('admin.account.previewProfile')}
                </a>
              )}
            </div>
          </div>
          <ImagePicker
            value={imageId ?? null}
            onChange={(id) => setImageId(id)}
            mode="profile"
            className={classes.imagePicker}
          />
          <p className={classes.imageDescription}>
            {t('admin.account.imageDescription')}
          </p>
          <TextFieldNew
            label={t('admin.account.username')}
            value={username}
            onChange={setUsername}
            error={
              hasTakenAction && errors.username
                ? errors.username
                : hasUsernameUniquenessError
                ? t('admin.onboarding.username.uniquenessError')
                : undefined
            }
            autoCorrect={false}
            autoCapitalize={false}
          />
        </div>
      )}
      {(step === 'all' || step === 'personal') && (
        <div>
          <TextAreaNew
            label={t('admin.account.bio')}
            value={bio}
            onChange={setBio}
          />
          <TextFieldNew
            label={t('admin.account.websiteUrl')}
            value={homepage}
            onChange={(url) => setHomepage(url)}
            error={errors.homepage}
            className={classes.websiteField}
          />
          <div className={classes.phoneNumberInnerContainer}>
            <h3 className={classes.phoneNumberTitle}>
              {t('admin.account.phoneNumber.title')}
            </h3>
            <p className={classes.phoneNumberDescription}>
              <Trans t={t} i18nKey="admin.account.phoneNumber.description">
                <span className={classes.phoneNumberDescriptionBold} />
              </Trans>
            </p>
            <TextFieldNew
              type="tel"
              maxLength={10}
              label={t('admin.account.phoneNumber.label')}
              value={phoneNumber}
              onChange={setPhoneNumber}
              error={errors.phoneNumber}
            />
          </div>
        </div>
      )}
      {(step === 'all' || step === 'socials') && (
        <div>
          <h2 className={classes.sectionTitle}>{t('admin.account.socials')}</h2>
          <p className={classes.sectionDescription}>
            {t('admin.account.socialsDescription')}
          </p>
          {SOCIAL_URL_KEYS.map((key) => (
            <UrlField
              icon={SOCIAL_URL_ICONS[key]}
              label={t(`admin.account.${key}`)}
              value={input[key]}
              onChange={(url) => setSocialUrl(key, url)}
              key={key}
              error={errors[key]}
            />
          ))}
          <UrlField
            icon={'email'}
            label={t('admin.account.publicEmail')}
            value={publicEmail}
            onChange={setPublicEmail}
          />
        </div>
      )}
      {step === 'instagram' && (
        <UrlField
          icon={SOCIAL_URL_ICONS[INSTAGRAM_URL_KEY]}
          label={t(`admin.account.${INSTAGRAM_URL_KEY}`)}
          value={input[INSTAGRAM_URL_KEY]}
          onChange={(url) => setSocialUrl(INSTAGRAM_URL_KEY, url)}
          key={INSTAGRAM_URL_KEY}
          error={errors[INSTAGRAM_URL_KEY]}
        />
      )}
      {(step === 'all' || step === 'pins') && (
        <div>
          <h2 className={classes.sectionTitle}>
            {t('admin.account.userCustomLinks.title')}
          </h2>
          <p className={classes.sectionDescription}>
            <Trans t={t} i18nKey="admin.account.userCustomLinks.description">
              <span className={classes.socialsMax} />
            </Trans>
          </p>
          <CustomLinks
            userId={user.id}
            initialLinks={customLinks}
            className={classes.pins}
          />
        </div>
      )}
    </Page>
  )
)
